import React, { useState } from "react";
import "./PackingDashboard.css";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentDate, makePriorLotNumber } from "../../../helpers/Utils";
import Box from "../../../images/boxes.svg";
import BoxGreen from "../../../images/box_green.svg";

import TotalCase from "../../../images/totalCase.svg";
import TotalTrey from "../../../images/total_tray.svg";
import Carts from "../../../images/total_cart.svg";
import CartNo from "../../../images/cart_no.svg";
import KgGreen from "../../../images/kg_green.svg";
import TotalEmp from "../../../images/total_emp.svg";
import DashboardSection from "../../dashboard/components/DashboardSection";
import "../../dashboard/components/dashboard-sections.css";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { CycleIconSvg } from "../../../svgFiles/CycleIconSvg";
import { CartIconSvg } from "../../../svgFiles/CartIconSvg";
import { ScaleIconSvg } from "../../../svgFiles/ScaleIconSvg";
import { UsersIconSvg } from "../../../svgFiles/UsersIconSvg";

const PackingDashboard = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [isSingleDay, setIsSingleDay] = useState(true);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));

  const formatValueWithUnit = (label, value) => {
    const displayValue = value ?? 0;
    const strValue = String(displayValue);

    if (/%/.test(label)) {
      return /%/.test(strValue) ? strValue : `${strValue} %`;
    }
    if (/per\s*kg|\/\s*kg/i.test(label)) {
      return /per\s*kg/i.test(strValue) ? strValue : `${strValue} Per KG`;
    }
    if (/\bkg\b|kilo|weight/i.test(label)) {
      return /\bkg\b/i.test(strValue) ? strValue : `${strValue} KG`;
    }
    return strValue;
  };

  const handleApplyRange = ({ startDate, endDate }) => {
    const single = startDate === endDate;
    setIsSingleDay(single);
    if (single) setLotNumber(makePriorLotNumber(startDate));
  };

  // ── Section 1: Packing (Day) ─────────────────────────────────
  const packingMangoDayItems = [
    { label: "Cheeks 20 LB1", value: "0.00", icon: BoxGreen },
    { label: "Cheeks 10 LB", value: "0.00", icon: BoxGreen },
    { label: "Strips 20 LB", value: "0.00", icon: Box },
    { label: "Pasting 20LB", value: "0.00", icon: Box },
  ];

  // ── Section 2: Packing (Turn 2) ─────────────────────────────
  const packingMangoTurn2Items = [
    { label: "Total Boxes", value: "0.00", icon: TotalCase },
    { label: "Prime Cheek Boxes (10 pound)", value: "0.00", icon: BoxGreen },
    { label: "Prime Cheek Boxes (20 pound)", value: "0.00", icon: BoxGreen },
    { label: "Prime Strip Boxes (20 pound)", value: "0.00", icon: BoxGreen },
    { label: "Seconds Cheek Boxes (20 pound)", value: "0.00", icon: Box },
    { label: "Seconds Strip Boxes (20 pound)", value: "0.00", icon: Box },
    { label: "Tapitas Boxes (20 pound)", value: "0.00", icon: Box },
    { label: "Pieces Boxes (20 pound)", value: "0.00", icon: Box },
  ];

  const getTurn2MetricColor = (label) => {
    if (/seconds/i.test(label)) return "red";
    if (/total/i.test(label)) return "orange";
    return "green";
  };

  // ── Section 3: Carts + KG Mango Night ────────────────────────
  const cartsKgNightItems = [
    { label: "KG Strip",               value: "0.00", icon: KgGreen },
    { label: "KG Cheek",               value: "0.00", icon: KgGreen },
    { label: "KG Tapita",              value: "0.00", icon: KgGreen },
    { label: "Carts Complete",         value: "0.00", icon: Carts },
    { label: "Carts Not Complete",     value: "0.00", icon: CartNo, iconBg: "#FFC1C1" },
    { label: "Treys Cheeks Complete",  value: "0.00", icon: TotalTrey },
    { label: "Treys Tapitas",          value: "0.00", icon: TotalTrey },
    { label: "Treys Strips Complete",  value: "0.00", icon: TotalTrey },
    { label: "Treys Total Complete",   value: "0.00", icon: TotalTrey },
    { label: "Amount Of People Carts", value: "0.00", icon: TotalEmp },
    { label: "Carts Per Person",       value: "0.00", icon: Carts },
  ];

  const kgOutputItems    = cartsKgNightItems.slice(0, 3);   // KG Strip, Cheek, Tapita
  const cartStatusItems  = cartsKgNightItems.slice(3, 5);   // Carts Complete / Not Complete
  const treysItems       = cartsKgNightItems.slice(5, 9);   // Treys (4 items)
  const workforceItems   = cartsKgNightItems.slice(9, 11);  // People + Per Person

  return (
    <div>
      {/* ── Toolbar ───────────────────────────────────────────── */}
      <div className="packing_dashboard_toolbar mb-4 align-items-center">
        <h5 className="packing_dashboard_toolbar_title m-0">
          {_t("packing_overview", "Packing Overview")}
        </h5>

        <div className="packing_toolbar_right">
          {isSingleDay && (
            <div className="packing_filter_group">
              <label className="packing_filter_label">
                {_t("lot_number", "Lot Number")}
              </label>
              <div className="packing_lot_number_row">
                <div className="packing_lot_chip borderRedBtn cmn_btn btn-small">
                  #{lotNumber}
                </div>
              </div>
            </div>
          )}

          <div className="packing_filter_group">
            <label htmlFor="packing-date-range-filter" className="packing_filter_label">
              {_t("date_range", "Date Range")}
            </label>
            <DateRangeFilter setDate={setDate} onApplyRange={handleApplyRange} triggerId="packing-date-range-filter" />
          </div>
        </div>
      </div>

      {/* ── Section 1 ─────────────────────────────────────────── */}
      <div className="d-flex flex-column gap-4">
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <PackageIconSvg size={18} color="#60a5fa" />
              {_t("packing_day", "Packing (Day)")}
            </span>
          }
        >
          <div className="pp-brokering-grid py-0">
            {packingMangoDayItems.map((item, i) => (
              <div key={i} className="pp-brokering-box">
                <div className="pp-brokering-box-label">{_t(item.label, item.label)}</div>
                <div className="pp-brokering-box-value">{formatValueWithUnit(item.label, item.value)}</div>
              </div>
            ))}
          </div>
        </DashboardSection>

        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <CycleIconSvg size={18} color="#60a5fa" />
              {_t("packing_turn_2", "Packing (Turn 2)")}
            </span>
          }
        >
            <div className="db-trash-grid py-0">
              {packingMangoTurn2Items.map((item, i) => (
                <div key={i} className="db-trash-cell">
                  <span>{_t(item.label, item.label)}</span>
                  <span className={`db-trash-val ${getTurn2MetricColor(item.label)}`}>
                    {formatValueWithUnit(item.label, item.value)}
                  </span>
                </div>
              ))}
            </div>
        </DashboardSection>

        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <CartIconSvg size={18} color="#60a5fa" />
              {_t("carts_kg_mango_night", "Carts + KG Mango Night")}
            </span>
          }
        >
          <div className="db-avg-card">

            {/* Zone 1: KG Output — scannable tiles */}
            <div className="db-avg-header">
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <ScaleIconSvg size={15} color="#4ade80" />
                {_t("kg_output", "KG Output")}
              </span>
              <span style={{ fontSize: 12, color: "#667085", fontFamily: "'Public Sans', serif" }}>
                {_t("night_totals", "Night Totals")}
              </span>
            </div>
            <div className="pp-brokering-grid" style={{ padding: "16px 18px" }}>
              {kgOutputItems.map((item, i) => (
                <div key={i} className="pp-brokering-box" style={{ background: "#f0fdf4", borderColor: "#bbf7d0" }}>
                  <div className="pp-brokering-box-label" style={{ color: "#15803d" }}>{_t(item.label, item.label)}</div>
                  <div className="pp-brokering-box-value" style={{ color: "#16a34a" }}>{formatValueWithUnit(item.label, item.value)}</div>
                </div>
              ))}
            </div>

            {/* Zone 2: Cart Status — complete vs incomplete with color signal */}
            <div className="db-avg-header" style={{ borderTop: "1px solid #f0f1f3" }}>
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <CartIconSvg size={15} color="#60a5fa" />
                {_t("cart_status", "Cart Status")}
              </span>
            </div>
            <div className="db-inner-metric-row">
              <div className="db-inner-metric">
                <div className="db-metric-label">{_t("carts_complete", "Carts Complete")}</div>
                <div className="db-metric-value" style={{ color: "#16a34a" }}>{formatValueWithUnit(cartStatusItems[0]?.label || "", cartStatusItems[0]?.value)}</div>
              </div>
              <div className="db-inner-metric">
                <div className="db-metric-label">{_t("carts_not_complete", "Carts Not Complete")}</div>
                <div className="db-metric-value" style={{ color: "#e53935" }}>{formatValueWithUnit(cartStatusItems[1]?.label || "", cartStatusItems[1]?.value)}</div>
              </div>
            </div>

            {/* Zone 3: Treys Breakdown */}
            <div className="db-avg-header" style={{ borderTop: "1px solid #f0f1f3" }}>
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <PackageIconSvg size={15} color="#fb923c" />
                {_t("treys_breakdown", "Treys Breakdown")}
              </span>
            </div>
            <div className="db-trash-grid" style={{ padding: "16px 18px" }}>
              {treysItems.map((item, i) => {
                const isTotal = /total/i.test(item.label);
                return (
                  <div key={i} className="db-trash-cell" style={isTotal ? { background: "#fff8f5", borderColor: "#f5d9c8" } : {}}>
                    <span>{_t(item.label, item.label)}</span>
                    <span className={`db-trash-val ${isTotal ? "orange" : "green"}`}>{formatValueWithUnit(item.label, item.value)}</span>
                  </div>
                );
              })}
            </div>

            {/* Zone 4: Workforce Efficiency */}
            <div className="db-avg-header" style={{ borderTop: "1px solid #f0f1f3" }}>
              <span className="db-avg-header-title d-flex align-items-center gap-2">
                <UsersIconSvg size={15} color="#60a5fa" />
                {_t("workforce", "Workforce")}
              </span>
            </div>
            <div className="db-inner-metric-row">
              {workforceItems.map((item, i) => (
                <div key={i} className="db-inner-metric">
                  <div className="db-metric-label">{_t(item.label, item.label)}</div>
                  <div className="db-metric-value" style={{ color: "#ee7225" }}>{formatValueWithUnit(item.label, item.value)}</div>
                </div>
              ))}
            </div>

          </div>
        </DashboardSection>

      </div>
    </div>
  );
};

export default PackingDashboard;
